import React, { useEffect, useState, useMemo } from 'react';
import { filter, flattenDeep, groupBy, isObject, uniqBy } from 'lodash';
import QuoteGridRow from '../../components/sms/QuoteGridRow';
import { PAGE_TYPES } from './data';
import TableSortButton from '../../components/guidedInterface/components/TableSortButton';
import { WMSFetch } from '../../utils/common-api-calls';
import logError from '../../../utils/errorUtil';
import Notification from '../../../components/Notification';
import MultiSelect from '../../../portal/components/MultiSelect';
import { ArrowPathIcon } from '@heroicons/react/20/solid';

function getTodayISO() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const tzOffset = d.getTimezoneOffset() * 60000;
    const local = new Date(d.getTime() - tzOffset);
    return local.toISOString().slice(0, 10);
}

export default function WMS_QuoteGrid({
    data,
    selectedRows,
    setSelectedRows,
    setActivePage,
    loading,
    setLoading,
    filterDate,
    setFilterDate,
    filters,
    setFilters,
    userList,
    arrowDirections,
    setArrowDirections,
    currentUser,
    partsWithInventory,
    setPartsWithInventory,
    showInStock,
    setShowInStock,
    sources,
    conditionCodes,
    mmddyyyyToIso,
    onRefresh,
    expandAll,
    setExpandAll,
}) {
    function textIncludes(stringValue, searchValue) {
        const normalize = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');
        const normalizedText = normalize(stringValue);
        const terms = Array.isArray(searchValue) ? searchValue : [searchValue];
        return terms.length === 0 || terms.some((n) => normalizedText.includes(normalize(n)));
    }

    // ---- RESTORE FILTERS ----
    const todayISO = getTodayISO();

    const [showFullyProcessedGroups, setShowFullyProcessedGroups] = useState(false);
    const [pendingDate, setPendingDate] = useState(filterDate);
    const [showNotification, setShowNotification] = useState({ shown: false, status: null, title: '', message: '' });
    const [verifyingLines, setVerifyinLines] = useState(false);

    const PROCESSED_FRIENDLY_LABELS = {
        LOCKED: 'Locked (Cannot Edit)',
        IGNORE: 'Ignored',
        SEND_PARTIAL: 'Partial Sent',
        SELECT: 'In Process',
        NO_QUOTE: 'No Quote',
    };

    const [expanded, setExpanded] = useState(null);

    const handleFilterDateChange = (newDate) => {
        setPendingDate(newDate);
        setFilterDate(newDate);
    };

    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
    };

    // ---- FILTER LOGIC ----
    function isInDateRange(rowDateStr, filterDateStr) {
        if (!filterDateStr) return true;
        const rowDate = new Date(mmddyyyyToIso(rowDateStr));
        const end = new Date(filterDateStr);
        const start = new Date(filterDateStr);
        start.setDate(end.getDate() - 7);
        return rowDate >= start;
    }

    const KNOWN_TYPES = new Set(['Airline', 'MRO', 'Temp Customer']);
    const normalizeCustomerType = (val) => {
        const s = (val ?? '').toString().trim();
        return KNOWN_TYPES.has(s) ? s : 'Broker';
    };

    // --- All filters (except special cases) should be assumed to be arrays for consistency ---
    function matchesFilters(row) {
        return Object.entries(filters).every(([field, raw]) => {
            const values = (Array.isArray(raw) ? raw : [raw]).filter((v) => v != null && String(v).trim() !== '');
            if (values.length === 0) return true;

            switch (field) {
                case 'customerType': {
                    const wanted = new Set(values);
                    const norm = normalizeCustomerType(row.customerType);
                    return wanted.has(norm);
                }

                case 'CreatedDate': {
                    const selectedDate = values[0];
                    return mmddyyyyToIso(row.CreatedDate) === selectedDate;
                }

                case 'quotes':
                case 'quantityRequested':
                    return true;

                case 'processed': {
                    const set = new Set(values);
                    if (set.has('yes')) return !!row.processed;
                    if (set.has('no')) return !row.processed;
                    return true;
                }

                case 'conditionCodeToDisplay': {
                    const wantsNull = values.includes('null');
                    const wanted = new Set(values.filter((v) => v !== 'null'));
                    const hasNull = wantsNull && !row.conditionCodeToDisplay;
                    const hasCode = !!row.conditionCodeToDisplay && wanted.has(row.conditionCodeToDisplay);
                    return hasNull || hasCode;
                }

                case 'salesAgentName':
                case 'accountOwnerName': {
                    const set = new Set(values);
                    const wantsBlank = set.has('__blank__');
                    const isBlank = !row[field] || row[field] === '';
                    return (wantsBlank && isBlank) || (row[field] && set.has(row[field]));
                }

                case 'partName':
                    return values.some((v) => textIncludes(row.partName, v));
                case 'customer':
                    return values.some((v) => textIncludes(row.customer, v));
                case 'quoteId':
                    return values.some((v) => textIncludes(row.quoteNumber, v));
                case 'inscor__Quote_Source__c':
                    return values.some((v) => textIncludes(row.inscor__Quote_Source__c, v));

                default: {
                    const cell = row[field];
                    return Array.isArray(cell) ? cell.some((x) => values.includes(x)) : values.includes(String(cell));
                }
            }
        });
    }

    let filteredRows = data.filter((row) => isInDateRange(row.CreatedDate, filterDate) && matchesFilters(row));
    let grouped = groupBy(filteredRows, 'partName');

    let sortedEntries = Object.entries(grouped);
    const activeSortKey = Object.entries(arrowDirections).find(([, v]) => v.direction !== null)?.[0];
    const activeSortDir = arrowDirections[activeSortKey]?.direction;

    if (activeSortKey) {
        sortedEntries.sort(([aKey, aRows], [bKey, bRows]) => {
            let aValue, bValue;

            switch (activeSortKey) {
                case 'partName':
                    aValue = aKey;
                    bValue = bKey;
                    break;
                case 'quotes':
                    aValue = aRows.length;
                    bValue = bRows.length;
                    break;
                case 'quantityRequested':
                    aValue = aRows.reduce((sum, r) => sum + Number(r.quantityRequested || 0), 0);
                    bValue = bRows.reduce((sum, r) => sum + Number(r.quantityRequested || 0), 0);
                    break;
                case 'customerContractPrice':
                    aValue = parseFloat(aRows[0]?.ccpPrice || 0);
                    bValue = parseFloat(bRows[0]?.ccpPrice || 0);
                    break;
                case 'listPrice':
                    aValue = parseFloat(aRows[0]?.listPrice || 0);
                    bValue = parseFloat(bRows[0]?.listPrice || 0);
                    break;
                default:
                    return 0;
            }

            if (typeof aValue === 'string') {
                return activeSortDir === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            return activeSortDir === 'asc' ? aValue - bValue : bValue - aValue;
        });
    }
    if (filters.quotes) {
        const quotesNumber = parseInt(filters.quotes, 10);
        grouped = Object.fromEntries(Object.entries(grouped).filter(([partName, rows]) => rows.length >= quotesNumber));
    }
    if (filters.quantityRequested) {
        const quantityFilter = Number(filters.quantityRequested);
        grouped = Object.fromEntries(
            Object.entries(grouped).filter(([partName, rows]) => {
                const totalQuantity = rows.reduce((sum, r) => sum + Number(r.quantityRequested || 0), 0);
                return totalQuantity >= quantityFilter;
            })
        );
    }

    const quotePartTotals = {};
    const quotePartProcessed = {};

    data.forEach((row) => {
        const qNum = row.quoteNumber;
        const part = row.partId;
        if (!qNum || !part) return;

        // Always add part to totals set
        if (!quotePartTotals[qNum]) quotePartTotals[qNum] = new Set();
        quotePartTotals[qNum].add(part);

        // If processed, also add to processed set
        if (!quotePartProcessed[qNum]) quotePartProcessed[qNum] = new Set();
        if (row.requestedDate) {
            quotePartProcessed[qNum].add(part);
        }
    });

    const displayedEntries = sortedEntries.filter(([partName, rows]) => {
        const safePartsWithInventory = Array.isArray(partsWithInventory) ? partsWithInventory : [];
        const partsWithInvSet = new Set(safePartsWithInventory);
        if (showInStock && !partsWithInvSet.has(partName)) return false;
        if (!rows.length) return false;

        if (filterDate && !rows.some((row) => mmddyyyyToIso(row.CreatedDate) === filterDate)) return false;

        const processedCount = rows.filter((r) => r.selectedState || r.tempLock).length;
        const totalCount = rows.length;

        if (!showFullyProcessedGroups && processedCount === totalCount) {
            return false;
        }

        return true;
    });

    const flattenedQuoteLines = flattenDeep(displayedEntries);
    const quoteLinesOnly = filter(flattenedQuoteLines, isObject);
    const uniqueQuotes = uniqBy(quoteLinesOnly, 'quoteNumber');
    const numUniqueQuotes = uniqueQuotes?.length;

    const sourceOptions = [...sources.map((s) => ({ label: s, value: s }))];

    const customerTypeOptions = [
        { label: 'Airline', value: 'Airline' },
        { label: 'MRO', value: 'MRO' },
        { label: 'Broker', value: 'Broker' },
        { label: 'Temp Customer', value: 'Temp Customer' },
    ];

    const ownerOptions = [
        ...userList.sort((a, b) => a.Name.localeCompare(b.Name)).map((u) => ({ label: u.Name, value: u.Name })),
    ];

    const agentOptions = [
        { label: '(Blank)', value: '__blank__' },
        ...userList.sort((a, b) => a.Name.localeCompare(b.Name)).map((u) => ({ label: u.Name, value: u.Name })),
    ];

    const conditionOptions = conditionCodes.map((code) => ({
        label: code === 'NULL' ? 'NULL' : code,
        value: code === 'NULL' ? 'null' : code,
    }));

    const outerColumns = [
        { key: 'partName', label: 'Part Number', width: '' },
        { key: 'quotes', label: `Quotes${numUniqueQuotes ? ` [${numUniqueQuotes}]` : ''}`, width: '' },
        { key: 'customerContractPrice', label: 'CCP', width: '' },
        { key: 'listPrice', label: 'List Price', width: '' },
        // { key: 'accountOwnerName', label: 'Account Owner', width: '' },
        // { key: 'salesAgentName', label: 'Sales Agent', width: '' },
        // { key: 'selected', label: 'Selected', width: '' },
        // { key: 'customer', label: 'Customer', width: '' },
        { key: 'quantityRequested', label: 'Quantity', width: '' },
        // { key: 'customerType', label: 'Type', width: '' },
        // { key: 'quoteId', label: 'Quote Number', width: '' },
        // { key: 'CreatedDate', label: 'Created Date', width: '' },
        // { key: 'inscor__Quote_Source__c', label: 'Source', width: '' },
        // { key: 'inscor__Status__c', label: 'Status', width: '' },
        // { key: 'conditionCodeToDisplay', label: 'Condition', width: '' },
        // { key: 'inscor__Temp_Priority_Code_Name__c', label: 'Priority', width: '' },
        // { key: 'quoteExclusionReason', label: 'Quote Exclusion', width: '' },
        // { key: 'partExclusionReason', label: 'Part Exclusion', width: '' },
        // { key: 'Parts_on_Quote', label: 'Parts on Quote', width: '' },
        { key: 'processed', label: 'Processed', width: '' },
    ];

    const expandedColumns = [
        { key: 'accountOwnerName', label: 'Account Owner' },
        { key: 'salesAgentName', label: 'Sales Agent' },
        { key: 'selected', label: 'Selected' },
        { key: 'customer', label: 'Customer' },
        { key: 'customerType', label: 'Type' },
        { key: 'quoteId', label: 'Quote Number' },
        { key: 'CreatedDate', label: 'Created Date' },
        { key: 'inscor__Quote_Source__c', label: 'Source' },
        { key: 'inscor__Status__c', label: 'Status' },
        { key: 'conditionCodeToDisplay', label: 'Condition' },
        { key: 'inscor__Temp_Priority_Code_Name__c', label: 'Priority' },
        { key: 'quoteExclusionReason', label: 'Quote Exclusion', width: '230px' },
        { key: 'partExclusionReason', label: 'Part Exclusion', width: '230px' },
        { key: 'Parts_on_Quote', label: 'Parts on Quote' },
        { key: 'selectedState', label: 'Processed' },
    ];

    const filterData = [
        { key: 'partName', label: 'Part Number', width: '150px' },
        { key: 'customer', label: 'Customer', width: '120px' },
        { key: 'customerType', label: 'Type', width: '80px' },
        { key: 'quoteId', label: 'Quote Number', width: '130px' },
        { key: 'inscor__Quote_Source__c', label: 'Source', width: '80px' },
        { key: 'conditionCodeToDisplay', label: 'Condition', width: '90px' },
        { key: 'accountOwnerName', label: 'Account Owner', width: '150px' },
        { key: 'salesAgentName', label: 'Sales Agent', width: '150px' },
    ];

    const skipKeys = ['partName', 'quotes', 'customerContractPrice', 'listPrice'];

    const allPartsForDateWindow = useMemo(() => {
        const rowsInDate = data.filter((row) => isInDateRange(row.CreatedDate, filterDate));
        const byPart = groupBy(rowsInDate, 'partName');

        return Object.entries(byPart).map(([partName, rows]) => ({
            partName,
            id: rows[0]?.partId,
        }));
    }, [data, filterDate]);

    const handleInventoryOnHandCheck = async (checked) => {
        try {
            if (!checked) {
                setShowInStock(false);
                return;
            }

            setLoading(true);

            const body = { parts: allPartsForDateWindow };

            const response = await WMSFetch('wms/sms/get-parts-with-inventory', body, currentUser);
            const partNames = response?.partNamesWithInventory ?? [];

            setPartsWithInventory(partNames);
            setShowInStock(true);
        } catch (error) {
            logError(error, 'SMS: handleInventoryOnHandCheck');
        } finally {
            setLoading(false);
        }
    };

    const closeNotification = () => {
        setShowNotification({ shown: false, status: null, title: '', message: '' });
    };

    const toggleExpand = (partName, rows) => {
        setExpanded((prev) => {
            if (prev !== partName) {
                setSelectedRows(
                    rows.map((row) => ({
                        partName,
                        quoteLineId: row.quoteLineId,
                        partId: row.partId,
                        partKeyword: row.keyword,
                    }))
                );
                return partName;
            } else {
                setSelectedRows([]);
                return null;
            }
        });
    };

    function handleCheckbox(partName, quoteLineId, partId) {
        setSelectedRows((prev) => {
            const exists = prev.find((item) => item.partName === partName && item.quoteLineId === quoteLineId);
            if (exists) {
                return prev.filter((item) => !(item.partName === partName && item.quoteLineId === quoteLineId));
            } else {
                return [...prev, { partName, quoteLineId, partId }];
            }
        });
    }

    function handleSelectAllForGroup(partName, rows) {
        setSelectedRows((prev) => {
            const selectableRows = rows.filter((r) => !r.tempLock && !r.requestedDate);

            if (selectableRows.length === 0) return prev;

            const isRowSelected = (r) =>
                prev.some((item) => item.partName === partName && item.quoteLineId === r.quoteLineId);

            const allSelectableAlreadySelected = selectableRows.every(isRowSelected);

            if (allSelectableAlreadySelected) {
                return prev.filter(
                    (item) => !(item.partName === partName && selectableRows.some((r) => r.quoteLineId === item.quoteLineId))
                );
            }

            const next = [...prev];
            selectableRows.forEach((r) => {
                if (!isRowSelected(r)) {
                    next.push({
                        partName,
                        quoteLineId: r.quoteLineId,
                        partId: r.partId,
                    });
                }
            });

            return next;
        });
    }

    async function handleSubmitSelected() {
        try {
            setVerifyinLines(true);
            const body = {
                quoteLineIds: selectedRows.map((row) => row.quoteLineId),
            };
            const quoteLineCheck = await WMSFetch('wms/sms/check-for-temp-lock', body, currentUser);
            if (!quoteLineCheck || quoteLineCheck?.error) throw Error();

            if (quoteLineCheck?.message === 'Has locked lines') {
                setShowNotification({
                    shown: true,
                    status: 'error',
                    title: 'The selected quote lines are currently in use.',
                    message:
                        'The selected quote lines are in use or locked by another user. Please try a different set of quote lines or refresh the page.',
                });
                return;
            }
            setActivePage(PAGE_TYPES.QUOTE_SUBMISSION);
        } catch (error) {
            setShowNotification({
                shown: true,
                status: 'error',
                title: 'Error verifying quote lines',
                message:
                    'There has been an error verifying quote lines. Please refresh the page and if the error persists let your manager know.',
            });
        } finally {
            setVerifyinLines(false);
        }
    }

    function formatCurrency(amount) {
        if (amount == null || isNaN(amount)) return '';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(amount);
    }

    useEffect(() => {
        if (showNotification.shown) {
            setTimeout(() => {
                closeNotification();
            }, 5_000);
        }
    }, [showNotification]);

    return (
        <div className='flex flex-col h-[calc(100vh-64px)]'>
            <Notification
                showSavedNotification={showNotification.shown}
                setShowSavedNotification={closeNotification}
                status={showNotification.status}
                title={showNotification.title}
                desc={showNotification.message}
            />
            <div className='flex items-center px-5 py-4 shrink-0'>
                <div className='grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 w-full'>
                    {filterData.map((col) => (
                        <div className='flex flex-col w-full' key={col.key}>
                            <label className='mb-1 text-sm font-medium text-gray-700'>{col.label}</label>

                            {col.key === 'conditionCodeToDisplay' ? (
                                <MultiSelect
                                    value={
                                        Array.isArray(filters.conditionCodeToDisplay) ? filters.conditionCodeToDisplay : []
                                    }
                                    onChange={(vals) => handleFiltersChange({ ...filters, conditionCodeToDisplay: vals })}
                                    options={conditionOptions}
                                    placeholder='ALL'
                                />
                            ) : col.key === 'inscor__Quote_Source__c' ? (
                                <MultiSelect
                                    value={
                                        Array.isArray(filters.inscor__Quote_Source__c) ? filters.inscor__Quote_Source__c : []
                                    }
                                    onChange={(vals) =>
                                        handleFiltersChange({
                                            ...filters,
                                            inscor__Quote_Source__c: vals.includes('__ALL__')
                                                ? []
                                                : vals.filter((v) => v !== '__ALL__'),
                                        })
                                    }
                                    options={sourceOptions}
                                    placeholder='ALL'
                                />
                            ) : col.key === 'customerType' ? (
                                <MultiSelect
                                    value={Array.isArray(filters.customerType) ? filters.customerType : []}
                                    onChange={(vals) =>
                                        setFilters((f) => ({
                                            ...f,
                                            customerType: vals.includes('__ALL__')
                                                ? []
                                                : vals.filter((v) => v !== '__ALL__'),
                                        }))
                                    }
                                    options={customerTypeOptions}
                                    placeholder='ALL'
                                />
                            ) : col.key === 'quoteId' ? (
                                <input
                                    type='text'
                                    value={filters.quoteId || ''}
                                    onChange={(e) => handleFiltersChange({ ...filters, quoteId: e.target.value })}
                                    className='h-[44px] px-3 border border-gray-300 rounded text-black'
                                />
                            ) : col.key === 'accountOwnerName' ? (
                                <MultiSelect
                                    value={Array.isArray(filters.accountOwnerName) ? filters.accountOwnerName : []}
                                    onChange={(vals) =>
                                        handleFiltersChange({
                                            ...filters,
                                            accountOwnerName: vals.includes('__ALL__')
                                                ? []
                                                : vals.filter((v) => v !== '__ALL__'),
                                        })
                                    }
                                    options={ownerOptions}
                                    placeholder='ALL'
                                />
                            ) : col.key === 'salesAgentName' ? (
                                <MultiSelect
                                    value={Array.isArray(filters.salesAgentName) ? filters.salesAgentName : []}
                                    onChange={(vals) =>
                                        handleFiltersChange({
                                            ...filters,
                                            salesAgentName: vals.includes('__ALL__')
                                                ? []
                                                : vals.filter((v) => v !== '__ALL__'),
                                        })
                                    }
                                    options={agentOptions}
                                    placeholder='ALL'
                                />
                            ) : (
                                <input
                                    type='text'
                                    value={filters[col.key] || ''}
                                    onChange={(e) => handleFiltersChange({ ...filters, [col.key]: e.target.value })}
                                    className='h-[44px] px-3 border border-gray-300 rounded text-black'
                                />
                            )}
                        </div>
                    ))}
                    <div className='flex flex-col'>
                        <label className='mb-1 text-sm font-medium text-gray-700'>Date</label>
                        <input
                            type='date'
                            value={pendingDate}
                            max={todayISO}
                            onChange={(e) => handleFilterDateChange(e.target.value)}
                            className='h-[33px] px-3 border border-gray-300 rounded text-black hover:cursor-pointer'
                            onKeyDown={(e) => e.preventDefault()}
                            onPaste={(e) => e.preventDefault()}
                            onCut={(e) => e.preventDefault()}
                        />
                        <div className='flex gap-4 items-center mt-2 whitespace-nowrap'>
                            <label className='flex gap-1 items-center text-xs text-gray-800 whitespace-nowrap cursor-pointer select-none'>
                                <input
                                    type='checkbox'
                                    className='w-3.5 h-3.5'
                                    checked={showInStock}
                                    onChange={(e) => handleInventoryOnHandCheck(e.target.checked)}
                                />
                                Inventory On Hand
                            </label>

                            <label className='flex gap-1 items-center text-xs text-gray-800 whitespace-nowrap cursor-pointer select-none'>
                                <input
                                    type='checkbox'
                                    className='w-3.5 h-3.5'
                                    checked={expandAll}
                                    onChange={(e) => setExpandAll(e.target.checked)}
                                />
                                Show All Lines
                            </label>
                        </div>
                    </div>
                </div>
                <div className='flex items-center h-full'>
                    <button
                        type='button'
                        className='p-0 ml-2 text-lg leading-none text-blue-600 bg-transparent border-none hover:text-blue-800 disabled:cursor-not-allowed'
                        onClick={() => onRefresh(false)}
                        disabled={loading}
                        title='Refresh Data'
                    >
                        <ArrowPathIcon className='w-7 h-7' />
                    </button>
                </div>
            </div>
            <div className='flex border overflow-y-auto h-[calc(100vh-64px)]'>
                <article className='overflow-auto w-full border'>
                    <table className='relative w-full text-center table-fixed'>
                        <colgroup>
                            {outerColumns.map((col) => (
                                <col key={col.key} style={{ width: col.width }} />
                            ))}
                        </colgroup>
                        <thead className='sticky top-0 z-20 text-white bg-blue-primary'>
                            <tr className='text-center'>
                                {outerColumns.map((col) => {
                                    const isSortable = [
                                        'partName',
                                        'quotes',
                                        'quantityRequested',
                                        'customerContractPrice',
                                        'listPrice',
                                    ].includes(col.key);

                                    if (col.key === 'processed') {
                                        return (
                                            <th key={col.key} className='px-4 py-3 font-semibold text-center'>
                                                <div className='flex flex-col gap-1 justify-center items-center'>
                                                    <div className='flex gap-1 justify-center items-center'>
                                                        <span>{col.label}</span>
                                                    </div>
                                                    <label className='flex gap-1 items-center text-xs text-white'>
                                                        <input
                                                            type='checkbox'
                                                            checked={showFullyProcessedGroups}
                                                            onChange={(e) => setShowFullyProcessedGroups(e.target.checked)}
                                                        />
                                                        Show all
                                                    </label>
                                                </div>
                                            </th>
                                        );
                                    }

                                    return (
                                        <th key={col.key} className='px-4 py-3 font-semibold text-center'>
                                            <div className='flex gap-1 justify-center items-center'>
                                                <span>{col.label}</span>
                                                {isSortable && (
                                                    <TableSortButton
                                                        name={col.key}
                                                        arrowDirections={arrowDirections}
                                                        setArrowDirections={setArrowDirections}
                                                    />
                                                )}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        {loading ? (
                            <tbody>
                                <tr>
                                    <td colSpan={outerColumns.length}>
                                        <div className='flex flex-col w-full h-[calc(100vh-400px)] items-center justify-center'>
                                            <div className='h-28 w-28 border-b-[5px] border-e-[5px] mb-7 border-gray-500 rounded-full animate-spin out' />
                                            <p className='text-xl'>Retrieving Quotes</p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        ) : displayedEntries.length === 0 ? (
                            <tbody>
                                <tr>
                                    <td colSpan={outerColumns.length}>
                                        <div className='py-16 text-xl text-center text-gray-500'>No quotes found</div>
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                {displayedEntries.map(([partName, rows]) => {
                                    const mainRow = rows[0];

                                    const totalQuantity = rows.reduce((sum, r) => sum + Number(r.quantityRequested || 0), 0);

                                    const groupHasValidSelection = rows.some(
                                        (row) =>
                                            selectedRows.some(
                                                (sel) => sel.partName === partName && sel.quoteLineId === row.quoteLineId
                                            ) &&
                                            !row.tempLock &&
                                            !row.requestedDate
                                    );

                                    // Count processed rows in this group
                                    const processedCount = rows.filter((r) => r.selectedState || r.tempLock).length;
                                    const totalCount = rows.length;
                                    const isFullyProcessedGroup = processedCount === totalCount;
                                    const selectableRows = rows.filter((r) => !r.tempLock && !r.requestedDate);
                                    const allSelectableSelected =
                                        selectableRows.length > 0 &&
                                        selectableRows.every((r) =>
                                            selectedRows.some(
                                                (sel) => sel.partName === partName && sel.quoteLineId === r.quoteLineId
                                            )
                                        );

                                    return (
                                        <React.Fragment key={partName}>
                                            <QuoteGridRow
                                                row={{
                                                    ...mainRow,
                                                    quotes: rows.length,
                                                    processed: `${processedCount}/${totalCount}`,
                                                    customerContractPrice: formatCurrency(mainRow.ccpPrice),
                                                    listPrice: formatCurrency(mainRow.listPrice),
                                                }}
                                                columns={outerColumns}
                                                expanded={expandAll || expanded === partName}
                                                onToggle={() => toggleExpand(partName, rows)}
                                                totalQuantity={totalQuantity}
                                                grayOut={isFullyProcessedGroup}
                                                expandAll={expandAll}
                                            />
                                            {(expandAll || expanded === partName) && (
                                                <>
                                                    <tr>
                                                        <td colSpan={outerColumns.length} className='p-0'>
                                                            <div className={expandAll ? '' : 'overflow-y-auto max-h-72'}>
                                                                <table className='w-full text-center bg-white table-fixed'>
                                                                    <colgroup>
                                                                        {expandedColumns.map((col) => (
                                                                            <col
                                                                                key={col.key}
                                                                                style={{ width: col.width }}
                                                                            />
                                                                        ))}
                                                                    </colgroup>
                                                                    <thead className='sticky top-0 z-10 text-white bg-blue-primary'>
                                                                        <tr>
                                                                            {expandedColumns.map((col) => (
                                                                                <th
                                                                                    key={col.key}
                                                                                    className='px-4 py-2 font-semibold text-center'
                                                                                >
                                                                                    {col.key === 'selected' ? (
                                                                                        <div className='flex flex-col gap-1 items-center'>
                                                                                            <span>{col.label}</span>
                                                                                            {expandAll &&
                                                                                                selectableRows.length >
                                                                                                    0 && (
                                                                                                    <button
                                                                                                        type='button'
                                                                                                        className='text-[11px] font-medium text-blue-100 hover:text-white underline underline-offset-2 disabled:text-blue-300 disabled:no-underline'
                                                                                                        onClick={() =>
                                                                                                            handleSelectAllForGroup(
                                                                                                                partName,
                                                                                                                rows
                                                                                                            )
                                                                                                        }
                                                                                                        disabled={
                                                                                                            selectableRows.length ===
                                                                                                            0
                                                                                                        }
                                                                                                    >
                                                                                                        {allSelectableSelected
                                                                                                            ? 'Clear all'
                                                                                                            : 'Select all'}
                                                                                                    </button>
                                                                                                )}
                                                                                        </div>
                                                                                    ) : (
                                                                                        col.label
                                                                                    )}
                                                                                </th>
                                                                            ))}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(() => {
                                                                            const allRowsForPart = [...rows].sort((a, b) => {
                                                                                const dateA = new Date(
                                                                                    mmddyyyyToIso(a.CreatedDate)
                                                                                );
                                                                                const dateB = new Date(
                                                                                    mmddyyyyToIso(b.CreatedDate)
                                                                                );
                                                                                return dateB - dateA;
                                                                            });

                                                                            return allRowsForPart.map((row, i) => {
                                                                                const isChecked = selectedRows.some(
                                                                                    (item) =>
                                                                                        item.partName === partName &&
                                                                                        item.quoteLineId === row.quoteLineId
                                                                                );
                                                                                return (
                                                                                    <tr
                                                                                        key={row.quoteId + i}
                                                                                        className='text-center border-b last:!border-none'
                                                                                    >
                                                                                        {expandedColumns.map((col) => {
                                                                                            if (col.key === 'selected') {
                                                                                                return (
                                                                                                    <td
                                                                                                        key={col.key}
                                                                                                        className='text-center'
                                                                                                    >
                                                                                                        <input
                                                                                                            type='checkbox'
                                                                                                            checked={
                                                                                                                isChecked &&
                                                                                                                !row.tempLock &&
                                                                                                                !row.requestedDate
                                                                                                            }
                                                                                                            disabled={
                                                                                                                row.tempLock ||
                                                                                                                row.requestedDate
                                                                                                            }
                                                                                                            onChange={() =>
                                                                                                                handleCheckbox(
                                                                                                                    partName,
                                                                                                                    row.quoteLineId,
                                                                                                                    row.partId
                                                                                                                )
                                                                                                            }
                                                                                                        />
                                                                                                    </td>
                                                                                                );
                                                                                            }
                                                                                            if (skipKeys.includes(col.key)) {
                                                                                                return (
                                                                                                    <td
                                                                                                        key={col.key}
                                                                                                        className='text-center'
                                                                                                    ></td>
                                                                                                );
                                                                                            }

                                                                                            if (
                                                                                                col.key ===
                                                                                                    'quoteExclusionReason' ||
                                                                                                col.key ===
                                                                                                    'partExclusionReason'
                                                                                            ) {
                                                                                                const value = row[col.key];

                                                                                                return (
                                                                                                    <td
                                                                                                        key={col.key}
                                                                                                        className='px-3 py-2 align-top align-middle'
                                                                                                    >
                                                                                                        <div className='inline-block max-w-xs text-xs leading-snug whitespace-normal break-words'>
                                                                                                            {value ? (
                                                                                                                value
                                                                                                            ) : (
                                                                                                                <span className='italic text-gray-400'>
                                                                                                                    None
                                                                                                                </span>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    </td>
                                                                                                );
                                                                                            }
                                                                                            return (
                                                                                                <td
                                                                                                    key={col.key}
                                                                                                    className='py-2 text-center'
                                                                                                >
                                                                                                    {col.key ===
                                                                                                    'selectedState' ? (
                                                                                                        row.selectedState ? (
                                                                                                            PROCESSED_FRIENDLY_LABELS[
                                                                                                                row
                                                                                                                    .selectedState
                                                                                                            ] ||
                                                                                                            row.selectedState
                                                                                                        ) : row.tempLock &&
                                                                                                          !isNaN(
                                                                                                              new Date(
                                                                                                                  row.tempLock
                                                                                                              )
                                                                                                          ) ? (
                                                                                                            'Currently In Process'
                                                                                                        ) : (
                                                                                                            ''
                                                                                                        )
                                                                                                    ) : col.key ===
                                                                                                      'quoteId' ? (
                                                                                                        <div className='flex justify-center'>
                                                                                                            <a
                                                                                                                href={
                                                                                                                    row.quoteNumberLink
                                                                                                                }
                                                                                                                target='_blank'
                                                                                                                className='text-blue-600 underline cursor-pointer hover:text-blue-800'
                                                                                                            >
                                                                                                                {
                                                                                                                    row.quoteNumber
                                                                                                                }
                                                                                                            </a>
                                                                                                        </div>
                                                                                                    ) : col.key ===
                                                                                                      'Parts_on_Quote' ? (
                                                                                                        `${
                                                                                                            quotePartProcessed[
                                                                                                                row
                                                                                                                    .quoteNumber
                                                                                                            ]?.size || 0
                                                                                                        }/${
                                                                                                            quotePartTotals[
                                                                                                                row
                                                                                                                    .quoteNumber
                                                                                                            ]?.size || 0
                                                                                                        }`
                                                                                                    ) : (
                                                                                                        row[col.key]
                                                                                                    )}
                                                                                                </td>
                                                                                            );
                                                                                        })}
                                                                                    </tr>
                                                                                );
                                                                            });
                                                                        })()}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>

                                                    <tr>
                                                        <td colSpan={outerColumns.length - 2} className='bg-white'></td>
                                                        <td colSpan={2} className='py-2 bg-white'>
                                                            <div className='flex justify-end pr-4'>
                                                                <button
                                                                    onClick={() => handleSubmitSelected(partName)}
                                                                    disabled={!groupHasValidSelection || verifyingLines}
                                                                    className={`px-4 py-2 rounded-md whitespace-nowrap ${
                                                                        groupHasValidSelection
                                                                            ? 'text-white bg-blue-primary'
                                                                            : 'text-gray-500 bg-gray-300 cursor-not-allowed'
                                                                    }`}
                                                                >
                                                                    {verifyingLines
                                                                        ? '...Checking Lines'
                                                                        : 'Process Selected Quote Lines'}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        )}
                    </table>
                </article>
            </div>
        </div>
    );
}
